import React, { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Select } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  ComplaintsDataCollectionItemFragment,
  ComplaintsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useComplaintsCategories } from 'modules/complaints/hooks/useComplaintsCategories'
import { useUpdateComplaintCategory } from 'modules/complaints/hooks/useUpdateComplaintCategory'

interface ChangeCategoryModalProps {
  open: boolean
  onClose: () => void
  complaint: ComplaintsDataCollectionItemFragment
  onSuccess?: () => void
}

const ChangeCategoryModal = ({ open, onClose, complaint, onSuccess }: ChangeCategoryModalProps) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(() => {
    return complaint.category?.id ?? null
  })

  const { categories } = useComplaintsCategories()
  const { data: complaintsData } = useQuery(ComplaintsDocument)
  const { updateComplaintCategory, loading } = useUpdateComplaintCategory()

  const currentComplaint = complaintsData?.complaints?.complaintsConnection?.nodes?.find(
    (c) => c.id === complaint.id
  )

  useEffect(() => {
    if (open) {
      setSelectedCategoryId(currentComplaint?.category?.id || null)
    }
  }, [open, currentComplaint?.category?.id])

  const currentCategory = categories.find((cat) => cat.id === currentComplaint?.category?.id)

  const categoryOptions = [
    { value: 'none', label: i18n.t('complaints.category.none') },
    ...categories
      .filter((category) => category.enabled)
      .map((category) => ({
        value: category.id.toString(),
        label: category.name,
      })),
  ]

  const handleSubmit = async () => {
    try {
      const result = await updateComplaintCategory({
        complaintId: complaint.id,
        categoryId: selectedCategoryId,
      })

      const handledResult = handleResult(result, (_errors) => {})

      if (handledResult.success) {
        onClose()
        onSuccess?.()

        app.setFlash({
          type: 'correct',
          message: i18n.t('complaints.overview.category_updated_successfully'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('complaints.overview.category_update_error'),
      })
    }
  }

  return (
    <F0Dialog
      isOpen={open}
      onClose={onClose}
      title={i18n.t('complaints.overview.change_category_title')}
      primaryAction={{
        label: loading ? i18n.t('common.saving') : i18n.t('common.save'),
        onClick: handleSubmit,
        disabled: loading,
        loading: loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s12">
        <Input
          label={i18n.t('complaints.overview.existing_category')}
          value={currentCategory?.name || i18n.t('complaints.category.none')}
          disabled
        />
        <F0Select
          label={i18n.t('complaints.overview.new_category')}
          value={selectedCategoryId === null ? 'none' : selectedCategoryId.toString()}
          onChange={(value: string) => setSelectedCategoryId(value === 'none' ? null : value)}
          options={categoryOptions}
          placeholder={i18n.t('complaints.overview.select_category')}
        />
      </Box>
    </F0Dialog>
  )
}

export default ChangeCategoryModal
